import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  AccountDto,
  AccountListDto,
  AccountTreeNodeDto,
  AccountFilterDto,
  CreateAccountRequest,
  UpdateAccountRequest,
  EligibleFundingSource,
  AccountLocations,
} from '@/types/account.types';

export const accountApi = {
  /** GET /api/v1/accounts — paginated list (auto-scoped to caller's company) */
  /**
   * @param filter.locationId Show only accounts usable at that location (GAP-003). The server
   * ignores a location the caller cannot reach, so this cannot be used to see more.
   */
  getList: (filter?: AccountFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<AccountListDto>>>('/accounts', {
      params: filter,
    }),

  /** GET /api/v1/accounts/tree — full hierarchy tree for the company */
  getTree: () =>
    apiClient.get<ApiResponse<AccountTreeNodeDto[]>>('/accounts/tree'),

  /** GET /api/v1/accounts/{id} — full account record */
  getById: (id: string) =>
    apiClient.get<ApiResponse<AccountDto>>(`/accounts/${id}`),

  /** GET /api/v1/accounts/by-code/{code} — returns 404 when not found */
  getByCode: (code: string) =>
    apiClient.get<ApiResponse<AccountDto>>(
      `/accounts/by-code/${encodeURIComponent(code)}`,
    ),

  /** POST /api/v1/accounts — create a new account */
  create: (data: CreateAccountRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/accounts', data),

  /** PUT /api/v1/accounts/{id} — update account details */
  update: (id: string, data: UpdateAccountRequest) =>
    apiClient.put<ApiResponse<object>>(`/accounts/${id}`, data),

  /** DELETE /api/v1/accounts/{id} — soft-delete the account */
  remove: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/accounts/${id}`),

  /**
   * GET /api/v1/accounts/{id}/locations — the locations this account may be used at (GAP-003).
   *
   * `isRestricted: false` with an empty list means the account may be used anywhere, which is the
   * normal case. Sent explicitly so a screen never has to guess what an empty list means.
   */
  getLocations: (id: string) =>
    apiClient.get<ApiResponse<AccountLocations>>(`/accounts/${id}/locations`),

  /**
   * PUT /api/v1/accounts/{id}/locations — replace the set. An empty array lifts the restriction.
   *
   * The server enforces the same rule when a transaction is saved, so this decides what a screen
   * offers, not what the system permits.
   */
  setLocations: (id: string, locationIds: string[]) =>
    apiClient.put<ApiResponse<object>>(`/accounts/${id}/locations`, { locationIds }),

  /**
   * GET /api/v1/accounts/eligible-funding — where a voucher may take money from or put it.
   *
   * Bank methods return the bank accounts (kind BANK); cash methods return the cash GL accounts
   * (kind CASH), which are the only way to reach a till. The server enforces the same rule on
   * save, so this filters the list rather than being the control.
   */
  eligibleFunding: (params: {
    paymentMethod: string;
    forAp: boolean;
    locationId?: string | null;
    currencyCode?: string | null;
  }) =>
    apiClient.get<ApiResponse<EligibleFundingSource[]>>('/accounts/eligible-funding', {
      params: {
        paymentMethod: params.paymentMethod,
        forAp: params.forAp,
        locationId: params.locationId || undefined,
        currencyCode: params.currencyCode || undefined,
      },
    }),

  /**
   * GET /api/v1/accounts/allowed-payment-methods — the methods this company permits.
   *
   * The voucher's method dropdown used to list every method the enum knows. On a company whose
   * configuration bars a method in one direction, picking it left the operator with an empty,
   * disabled "Pay from" and no way forward. Advisory like eligibleFunding: the server refuses a
   * barred method on save whatever the screen shows.
   */
  allowedPaymentMethods: (forAp: boolean) =>
    apiClient.get<ApiResponse<string[]>>('/accounts/allowed-payment-methods', {
      params: { forAp },
    }),
};
